import * as THREE from 'three';

/** Hash noise for procedural textures (deterministic). */
function hash(x: number, y: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

function smoothNoise(x: number, y: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const u = fx * fx * (3 - 2 * fx);
  const v = fy * fy * (3 - 2 * fy);
  const a = hash(ix, iy);
  const b = hash(ix + 1, iy);
  const c = hash(ix, iy + 1);
  const d = hash(ix + 1, iy + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

function fbm(x: number, y: number, octaves = 4): number {
  let v = 0;
  let a = 0.5;
  let f = 1;
  for (let i = 0; i < octaves; i++) {
    v += a * smoothNoise(x * f, y * f);
    f *= 2;
    a *= 0.5;
  }
  return v;
}

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}

export type PaletteKey =
  | 'tunnelConcrete'
  | 'pitchGrass'
  | 'pitchLine'
  | 'jerseyFabric'
  | 'ballLeather'
  | 'floodMetal'
  | 'skin'
  | 'shorts';

interface BakeOpts {
  size?: number;
  tint?: [number, number, number];
  scale?: number;
}

interface TextureSet {
  map: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
  metalnessMap: THREE.CanvasTexture;
  normalMap: THREE.CanvasTexture;
  aoMap: THREE.CanvasTexture;
}

const cache = new Map<string, THREE.MeshStandardMaterial>();

function makeCanvas(size: number) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  return { canvas, ctx };
}

function toTexture(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

function bakeHeightNormal(
  height: Float32Array,
  size: number,
  strength = 1.4,
): HTMLCanvasElement {
  const { canvas, ctx } = makeCanvas(size);
  const img = ctx.createImageData(size, size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const hL = height[y * size + ((x - 1 + size) % size)];
      const hR = height[y * size + ((x + 1) % size)];
      const hD = height[((y - 1 + size) % size) * size + x];
      const hU = height[((y + 1) % size) * size + x];
      let nx = (hL - hR) * strength;
      let ny = (hD - hU) * strength;
      let nz = 1;
      const len = Math.hypot(nx, ny, nz) || 1;
      nx /= len;
      ny /= len;
      nz /= len;
      const p = i * 4;
      img.data[p] = (nx * 0.5 + 0.5) * 255;
      img.data[p + 1] = (ny * 0.5 + 0.5) * 255;
      img.data[p + 2] = (nz * 0.5 + 0.5) * 255;
      img.data[p + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

function bakeSet(
  size: number,
  paint: (
    albedo: Uint8ClampedArray,
    orm: Uint8ClampedArray,
    height: Float32Array,
    size: number,
  ) => void,
): TextureSet {
  const a = makeCanvas(size);
  const o = makeCanvas(size);
  const albedo = a.ctx.createImageData(size, size);
  const orm = o.ctx.createImageData(size, size);
  const height = new Float32Array(size * size);
  paint(albedo.data, orm.data, height, size);
  a.ctx.putImageData(albedo, 0, 0);
  o.ctx.putImageData(orm, 0, 0);
  const normalCanvas = bakeHeightNormal(height, size);

  const map = toTexture(a.canvas);
  const ormTex = toTexture(o.canvas);
  ormTex.colorSpace = THREE.NoColorSpace;

  // Split ORM into separate maps for MeshStandardMaterial convenience
  const rough = makeCanvas(size);
  const metal = makeCanvas(size);
  const ao = makeCanvas(size);
  const rImg = rough.ctx.createImageData(size, size);
  const mImg = metal.ctx.createImageData(size, size);
  const aoImg = ao.ctx.createImageData(size, size);
  for (let i = 0; i < size * size; i++) {
    const p = i * 4;
    const aoV = orm.data[p];
    const roughV = orm.data[p + 1];
    const metalV = orm.data[p + 2];
    rImg.data[p] = rImg.data[p + 1] = rImg.data[p + 2] = roughV;
    rImg.data[p + 3] = 255;
    mImg.data[p] = mImg.data[p + 1] = mImg.data[p + 2] = metalV;
    mImg.data[p + 3] = 255;
    aoImg.data[p] = aoImg.data[p + 1] = aoImg.data[p + 2] = aoV;
    aoImg.data[p + 3] = 255;
  }
  rough.ctx.putImageData(rImg, 0, 0);
  metal.ctx.putImageData(mImg, 0, 0);
  ao.ctx.putImageData(aoImg, 0, 0);

  const roughnessMap = toTexture(rough.canvas);
  roughnessMap.colorSpace = THREE.NoColorSpace;
  const metalnessMap = toTexture(metal.canvas);
  metalnessMap.colorSpace = THREE.NoColorSpace;
  const aoMap = toTexture(ao.canvas);
  aoMap.colorSpace = THREE.NoColorSpace;
  const normalMap = toTexture(normalCanvas);
  normalMap.colorSpace = THREE.NoColorSpace;

  // silence unused
  void ormTex;

  return { map, roughnessMap, metalnessMap, normalMap, aoMap };
}

function paintConcrete(
  albedo: Uint8ClampedArray,
  orm: Uint8ClampedArray,
  height: Float32Array,
  size: number,
  tint: [number, number, number],
  scale: number,
) {
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const p = i * 4;
      const n = fbm(x * scale * 0.04, y * scale * 0.04, 5);
      const crack = smoothNoise(x * 0.12, y * 0.12);
      const grit = hash(x * 0.7, y * 0.9);
      const shade = clamp01(0.22 + n * 0.35 + grit * 0.08 - crack * 0.04);
      albedo[p] = tint[0] * shade * 255;
      albedo[p + 1] = tint[1] * shade * 255;
      albedo[p + 2] = tint[2] * shade * 255;
      albedo[p + 3] = 255;
      const rough = clamp01(0.78 + n * 0.15 + grit * 0.05);
      orm[p] = (0.55 + n * 0.35) * 255; // AO
      orm[p + 1] = rough * 255;
      orm[p + 2] = 0;
      orm[p + 3] = 255;
      height[i] = n * 0.55 + crack * 0.15;
    }
  }
}

function paintGrass(
  albedo: Uint8ClampedArray,
  orm: Uint8ClampedArray,
  height: Float32Array,
  size: number,
  tint: [number, number, number],
  scale: number,
) {
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const p = i * 4;
      const blade = Math.abs(Math.sin(x * 0.9 * scale + fbm(x * 0.05, y * 0.05) * 4));
      const patch = fbm(x * 0.02 * scale, y * 0.02 * scale, 4);
      const wear = smoothNoise(x * 0.08, y * 0.08);
      const g = clamp01(0.35 + patch * 0.4 + blade * 0.12 - wear * 0.08);
      albedo[p] = tint[0] * (0.55 + wear * 0.25) * g * 255;
      albedo[p + 1] = tint[1] * g * 255;
      albedo[p + 2] = tint[2] * (0.7 + blade * 0.2) * g * 255;
      albedo[p + 3] = 255;
      orm[p] = (0.65 + patch * 0.25) * 255;
      orm[p + 1] = (0.82 + blade * 0.1) * 255;
      orm[p + 2] = 0;
      orm[p + 3] = 255;
      height[i] = blade * 0.35 + patch * 0.25;
    }
  }
}

function paintFabric(
  albedo: Uint8ClampedArray,
  orm: Uint8ClampedArray,
  height: Float32Array,
  size: number,
  tint: [number, number, number],
  scale: number,
) {
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const p = i * 4;
      const weave =
        (Math.sin(x * 0.85 * scale) * 0.5 + 0.5) *
        (Math.sin(y * 0.85 * scale) * 0.5 + 0.5);
      const fold = fbm(x * 0.03, y * 0.03, 3);
      const shade = clamp01(0.55 + weave * 0.2 + fold * 0.25);
      albedo[p] = tint[0] * shade * 255;
      albedo[p + 1] = tint[1] * shade * 255;
      albedo[p + 2] = tint[2] * shade * 255;
      albedo[p + 3] = 255;
      orm[p] = (0.7 + fold * 0.2) * 255;
      orm[p + 1] = (0.72 + weave * 0.12) * 255;
      orm[p + 2] = 0;
      orm[p + 3] = 255;
      height[i] = weave * 0.2 + fold * 0.35;
    }
  }
}

function paintLeather(
  albedo: Uint8ClampedArray,
  orm: Uint8ClampedArray,
  height: Float32Array,
  size: number,
  tint: [number, number, number],
  scale: number,
) {
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const p = i * 4;
      const grain = fbm(x * 0.08 * scale, y * 0.08 * scale, 4);
      const panel =
        Math.abs(Math.sin((x / size) * Math.PI * 4)) *
        Math.abs(Math.sin((y / size) * Math.PI * 3));
      const shade = clamp01(0.7 + grain * 0.2 - panel * 0.08);
      albedo[p] = tint[0] * shade * 255;
      albedo[p + 1] = tint[1] * shade * 255;
      albedo[p + 2] = tint[2] * shade * 255;
      albedo[p + 3] = 255;
      orm[p] = (0.75 + grain * 0.15) * 255;
      orm[p + 1] = (0.42 + grain * 0.2) * 255;
      orm[p + 2] = 8;
      orm[p + 3] = 255;
      height[i] = grain * 0.4 + panel * 0.2;
    }
  }
}

function paintMetal(
  albedo: Uint8ClampedArray,
  orm: Uint8ClampedArray,
  height: Float32Array,
  size: number,
  tint: [number, number, number],
  scale: number,
) {
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const p = i * 4;
      const scratch = hash(x * 1.7, y * 0.3);
      const n = fbm(x * 0.05 * scale, y * 0.05 * scale, 3);
      const shade = clamp01(0.55 + n * 0.3 + scratch * 0.05);
      albedo[p] = tint[0] * shade * 255;
      albedo[p + 1] = tint[1] * shade * 255;
      albedo[p + 2] = tint[2] * shade * 255;
      albedo[p + 3] = 255;
      orm[p] = (0.8 + n * 0.1) * 255;
      orm[p + 1] = (0.28 + scratch * 0.2) * 255;
      orm[p + 2] = 220;
      orm[p + 3] = 255;
      height[i] = n * 0.2 + scratch * 0.05;
    }
  }
}

function paintLine(
  albedo: Uint8ClampedArray,
  orm: Uint8ClampedArray,
  height: Float32Array,
  size: number,
) {
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const p = i * 4;
      const wear = fbm(x * 0.1, y * 0.1, 2);
      const v = clamp01(0.88 + wear * 0.08);
      albedo[p] = albedo[p + 1] = albedo[p + 2] = v * 255;
      albedo[p + 3] = 255;
      orm[p] = 220;
      orm[p + 1] = 180;
      orm[p + 2] = 0;
      orm[p + 3] = 255;
      height[i] = wear * 0.1;
    }
  }
}

const DEFS: Record<
  PaletteKey,
  {
    size: number;
    tint: [number, number, number];
    scale: number;
    kind: 'concrete' | 'grass' | 'fabric' | 'leather' | 'metal' | 'line' | 'skin';
    repeat?: number;
    roughness?: number;
    metalness?: number;
  }
> = {
  tunnelConcrete: {
    size: 512,
    tint: [0.18, 0.2, 0.19],
    scale: 1.2,
    kind: 'concrete',
    repeat: 4,
  },
  pitchGrass: {
    size: 512,
    tint: [0.14, 0.44, 0.22],
    scale: 1.25,
    kind: 'grass',
    repeat: 8,
  },
  pitchLine: {
    size: 128,
    tint: [0.9, 0.92, 0.88],
    scale: 1,
    kind: 'line',
    repeat: 1,
  },
  jerseyFabric: {
    size: 512,
    tint: [0.72, 0.12, 0.2],
    scale: 1.4,
    kind: 'fabric',
    repeat: 2,
    roughness: 0.82,
  },
  ballLeather: {
    size: 256,
    tint: [0.92, 0.92, 0.9],
    scale: 1,
    kind: 'leather',
    repeat: 1,
    roughness: 0.45,
  },
  floodMetal: {
    size: 256,
    tint: [0.55, 0.52, 0.45],
    scale: 1,
    kind: 'metal',
    repeat: 2,
    metalness: 0.85,
  },
  skin: {
    size: 256,
    tint: [0.78, 0.53, 0.35],
    scale: 1,
    kind: 'fabric',
    repeat: 1,
    roughness: 0.55,
  },
  shorts: {
    size: 256,
    tint: [0.08, 0.12, 0.18],
    scale: 1.2,
    kind: 'fabric',
    repeat: 1,
  },
};

function buildTextures(key: PaletteKey, opts: BakeOpts = {}): TextureSet {
  const def = DEFS[key];
  const size = opts.size ?? def.size;
  const tint = opts.tint ?? def.tint;
  const scale = opts.scale ?? def.scale;

  return bakeSet(size, (albedo, orm, height, s) => {
    switch (def.kind) {
      case 'concrete':
        paintConcrete(albedo, orm, height, s, tint, scale);
        break;
      case 'grass':
        paintGrass(albedo, orm, height, s, tint, scale);
        break;
      case 'fabric':
      case 'skin':
        paintFabric(albedo, orm, height, s, tint, scale);
        break;
      case 'leather':
        paintLeather(albedo, orm, height, s, tint, scale);
        break;
      case 'metal':
        paintMetal(albedo, orm, height, s, tint, scale);
        break;
      case 'line':
        paintLine(albedo, orm, height, s);
        break;
    }
  });
}

export function getMaterial(
  key: PaletteKey,
  opts: BakeOpts & { color?: string } = {},
): THREE.MeshStandardMaterial {
  const cacheKey = `${key}|${JSON.stringify(opts)}`;
  const hit = cache.get(cacheKey);
  if (hit) return hit;

  const def = DEFS[key];
  const tex = buildTextures(key, opts);
  const repeat = def.repeat ?? 1;
  for (const t of [
    tex.map,
    tex.roughnessMap,
    tex.metalnessMap,
    tex.normalMap,
    tex.aoMap,
  ]) {
    t.repeat.set(repeat, repeat);
  }

  const mat = new THREE.MeshStandardMaterial({
    map: tex.map,
    roughnessMap: tex.roughnessMap,
    metalnessMap: tex.metalnessMap,
    normalMap: tex.normalMap,
    aoMap: tex.aoMap,
    aoMapIntensity: 0.85,
    roughness: def.roughness ?? 0.75,
    metalness: def.metalness ?? 0.02,
    envMapIntensity: 0.65,
    color: opts.color ? new THREE.Color(opts.color) : new THREE.Color('#ffffff'),
  });
  mat.normalScale = new THREE.Vector2(0.85, 0.85);
  cache.set(cacheKey, mat);
  return mat;
}

export function disposeMaterials() {
  for (const mat of cache.values()) {
    mat.map?.dispose();
    mat.roughnessMap?.dispose();
    mat.metalnessMap?.dispose();
    mat.normalMap?.dispose();
    mat.aoMap?.dispose();
    mat.dispose();
  }
  cache.clear();
}
